import type { ResumeSectionType } from '@applicator/shared'

import type { OptimizationBlockState } from './types'

export const MICRO_SECTION_BLOCK_ID = 'section:micro_sections'

const PARALLEL_SECTION_IDS: ResumeSectionType[] = [
  'contact_info',
  'target_title',
  'professional_summary',
  'skills',
  'education',
  'projects',
]

const SECTION_LABELS: Record<ResumeSectionType, string> = {
  contact_info: 'Contact Information',
  target_title: 'Target Title',
  professional_summary: 'Professional Summary',
  work_experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  interests: 'Interests',
  certifications: 'Certifications',
  awards_scholarships: 'Awards & Scholarships',
  projects: 'Projects',
}

export function slugCompany(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'company'
  )
}

export function sectionBlockId(sectionType: ResumeSectionType): string {
  return `section:${sectionType}`
}

export function workExperienceBlockId(companyName: string): string {
  return `work_experience:${slugCompany(companyName)}`
}

export function buildInitialSectionBlocks(): OptimizationBlockState[] {
  const base = PARALLEL_SECTION_IDS.map((sectionType) => ({
    block_id: sectionBlockId(sectionType),
    label: SECTION_LABELS[sectionType] || sectionType,
    section_type: sectionType,
    status: 'pending' as const,
  }))
  base.push({
    block_id: MICRO_SECTION_BLOCK_ID,
    label: 'Certifications & Interests',
    section_type: 'certifications',
    status: 'pending',
  })
  return base
}

export function blockIdForSection(sectionType: ResumeSectionType): string | null {
  if (sectionType === 'work_experience') return null
  if (sectionType === 'certifications' || sectionType === 'interests') {
    return MICRO_SECTION_BLOCK_ID
  }
  if (!PARALLEL_SECTION_IDS.includes(sectionType)) return null
  return sectionBlockId(sectionType)
}

/** True when at least one included work-experience bullet has text (matches API extract_wx_chunks). */
export function hasWorkExperienceBullets(workExperience: unknown): boolean {
  if (!Array.isArray(workExperience)) return false
  for (const co of workExperience) {
    if (!co || typeof co !== 'object') continue
    const company = co as Record<string, unknown>
    const rolesRaw = company.roles ?? company.positions
    if (!Array.isArray(rolesRaw)) continue
    for (const role of rolesRaw) {
      if (!role || typeof role !== 'object') continue
      const r = role as Record<string, unknown>
      const bulletsRaw = r.bulletPoints ?? r.bullets
      if (!Array.isArray(bulletsRaw)) continue
      for (const bullet of bulletsRaw) {
        if (!bullet || typeof bullet !== 'object') continue
        const b = bullet as Record<string, unknown>
        if (b.included === false) continue
        const text = typeof b.text === 'string' ? b.text.trim() : ''
        if (text) return true
      }
    }
  }
  return false
}

export function companyNamesFromWorkExperience(
  workExperience: unknown,
  cap = 3
): string[] {
  if (!Array.isArray(workExperience)) return []
  const names: string[] = []
  for (const co of workExperience) {
    const name =
      co && typeof co === 'object' && typeof (co as { name?: string }).name === 'string'
        ? (co as { name: string }).name.trim()
        : ''
    if (name) names.push(name)
  }
  return names.slice(0, cap)
}

export function mergeWorkExperienceBlocks(
  base: OptimizationBlockState[],
  companyNames: string[]
): OptimizationBlockState[] {
  const wxBlocks = companyNames.map((name) => ({
    block_id: workExperienceBlockId(name),
    label: `Work Experience — ${name}`,
    section_type: 'work_experience' as const,
    company_name: name,
    status: 'pending' as const,
  }))
  return [...base, ...wxBlocks]
}
